import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

function WebHeader() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close menu on route change
  useEffect(() => { setMenuOpen(false); }, [location]);

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/jobs", label: "Jobs" },
  ];

  const isActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500;600&display=swap');

        .wh-header {
          position: sticky; top: 0; z-index: 999;
          background: ${scrolled ? "rgba(255,255,255,0.97)" : "#fff"};
          box-shadow: ${scrolled ? "0 4px 24px rgba(0,0,0,.09)" : "0 1px 0 #e8eaf0"};
          transition: box-shadow .3s, background .3s;
          font-family: 'DM Sans', sans-serif;
        }

        .wh-inner {
          max-width: 1200px; margin: 0 auto;
          padding: 0 24px;
          display: flex; align-items: center; justify-content: space-between;
          height: 70px;
        }

        .wh-logo img { height: 52px; display: block; }

        .wh-nav { display: flex; align-items: center; gap: 4px; }

        .wh-link {
          text-decoration: none; font-size: 14px; font-weight: 500;
          color: #475569; padding: 7px 14px; border-radius: 8px;
          transition: color .2s, background .2s; position: relative;
        }
        .wh-link:hover { color: #2563eb; background: #eff6ff; }
        .wh-link.active {
          color: #2563eb; font-weight: 700;
          background: #eff6ff;
        }
        .wh-link.active::after {
          content: ''; position: absolute; bottom: -2px; left: 50%;
          transform: translateX(-50%);
          width: 18px; height: 3px; background: #2563eb; border-radius: 2px;
        }

        .wh-login-btn {
          text-decoration: none;
          background: #2563eb; color: #fff !important;
          padding: 8px 20px; border-radius: 9px;
          font-size: 14px; font-weight: 700;
          transition: background .2s, transform .15s, box-shadow .2s;
          box-shadow: 0 2px 8px rgba(37,99,235,.25);
          margin-left: 8px;
        }
        .wh-login-btn:hover {
          background: #1d4ed8;
          transform: translateY(-1px);
          box-shadow: 0 4px 16px rgba(37,99,235,.35);
        }

        .wh-jobs-badge {
          background: #dcfce7; color: #16a34a;
          font-size: 10px; font-weight: 700; padding: 1px 7px;
          border-radius: 20px; margin-left: 5px; vertical-align: middle;
          text-transform: uppercase; letter-spacing: .4px;
        }

        /* Hamburger */
        .wh-hamburger {
          display: none; flex-direction: column; gap: 5px;
          background: none; border: none; cursor: pointer; padding: 6px;
          border-radius: 8px; transition: background .2s;
        }
        .wh-hamburger:hover { background: #f1f5f9; }
        .wh-hamburger span {
          display: block; width: 22px; height: 2px; background: #334155;
          border-radius: 2px; transition: all .3s;
        }
        .wh-hamburger.open span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
        .wh-hamburger.open span:nth-child(2) { opacity: 0; }
        .wh-hamburger.open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

        /* Mobile menu */
        .wh-mobile-menu {
          display: none; flex-direction: column;
          background: #fff; border-top: 1px solid #f1f5f9;
          padding: 12px 24px 20px;
          box-shadow: 0 8px 24px rgba(0,0,0,.08);
          animation: slideDown .22s ease;
        }
        @keyframes slideDown { from { opacity:0; transform:translateY(-8px) } to { opacity:1; transform:translateY(0) } }

        .wh-mobile-link {
          text-decoration: none; font-size: 15px; font-weight: 500;
          color: #334155; padding: 11px 12px; border-radius: 9px;
          transition: background .18s, color .18s; display: block;
        }
        .wh-mobile-link:hover, .wh-mobile-link.active {
          background: #eff6ff; color: #2563eb; font-weight: 700;
        }
        .wh-mobile-login {
          margin-top: 10px; text-align: center;
          background: #2563eb; color: #fff; text-decoration: none;
          padding: 12px; border-radius: 10px; font-weight: 700; font-size: 15px;
          display: block; transition: background .2s;
        }
        .wh-mobile-login:hover { background: #1d4ed8; color: #fff; }

        @media (max-width: 768px) {
          .wh-nav { display: none; }
          .wh-hamburger { display: flex; }
          .wh-mobile-menu { display: ${menuOpen ? "flex" : "none"}; }
        }
      `}</style>

      <header className="wh-header">
        <div className="wh-inner">
          {/* Logo */}
          <Link className="wh-logo" to="/">
            <img src="assets/images/logo1.png" alt="logo" />
          </Link>

          {/* Desktop Nav */}
          <nav className="wh-nav">
            {navLinks.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`wh-link${isActive(to) ? " active" : ""}`}
              >
                {label}
                {label === "Jobs" && <span className="wh-jobs-badge">Hiring</span>}
              </Link>
            ))}
            <Link to="/login" className="wh-login-btn">Login →</Link>
          </nav>

          {/* Hamburger */}
          <button
            className={`wh-hamburger${menuOpen ? " open" : ""}`}
            onClick={() => setMenuOpen(p => !p)}
            aria-label="Toggle menu"
          >
            <span /><span /><span />
          </button>
        </div>

        {/* Mobile Menu */}
        <div className="wh-mobile-menu">
          {navLinks.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className={`wh-mobile-link${isActive(to) ? " active" : ""}`}
            >
              {label}
              {label === "Jobs" && <span className="wh-jobs-badge">Hiring</span>}
            </Link>
          ))}
          <Link to="/login" className="wh-mobile-login">Login →</Link>
        </div>
      </header>
    </>
  );
}

export default WebHeader;
